import type { ImportsCreateResult, UploadsCreateData } from '@contracts'
import { SortingDetails } from 'components/table/table-types'
import { SetSortByPayload } from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import { AmbiguousURLType, ClientAndBrandName, ClientsListDataV5 } from './utils.d'
import { FEApiError } from './errorHandling'
import { DateTimeFormatOptions } from 'luxon'
import { PDP_GENERATOR_API_HEADERS, unknown } from './constants'

export const getDateAtTimeString = (created_at: string) => {
  if (isNaN(Date.parse(created_at))) {
    return 'INVALID DATE'
  }
  const dateCreated = new Date(created_at)
  const hourFormatOptions: DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric', hour12: true }

  return `${dateCreated.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' })} at ${dateCreated.toLocaleTimeString([], hourFormatOptions)}`
}

export const getUrlFromEndpoint = (endpointOrUrl: AmbiguousURLType) => {
  let url: AmbiguousURLType
  if (typeof endpointOrUrl === 'string') {
    url = createCsApiUrlFromEndpoint(endpointOrUrl)
  } else {
    url = endpointOrUrl
  }
  return new URL(url)
}

export function combineSearchParams(params1: URLSearchParams, params2: URLSearchParams) {
  let combined = new URLSearchParams(params1.toString())
  params2.forEach((queryValue, queryKey) => {
    combined.append(queryKey, queryValue)
  })
  return combined
}

/** DEPRECATED: Will be removed after WPP Open integration */
export const getAPIKey = (): string => {
  if (!process.env.REACT_APP_CS_API_KEY) throw new Error('API Key Not Found, Check Environment Variables')
  return process.env.REACT_APP_CS_API_KEY
}

export async function fetchCsApiData<T>(
  endpointOrUrl: AmbiguousURLType,
  fetchOptions: Partial<RequestInit>,
  retries: number = 0,
  responseType: 'json' | 'csv' = 'json',
  overrideErrorsHandling: boolean = false,
): Promise<T> {
  try {
    const requestInit: RequestInit = {
      ...fetchOptions,
      headers: {
        ...fetchOptions.headers,
        // TODO: Remove this on integration into WPP Open
        // 'cs-api-key': getAPIKey(),
      },
    }

    const url = getUrlFromEndpoint(endpointOrUrl)

    const res = await fetch(url, requestInit)

    let resData

    if (!res.ok && !overrideErrorsHandling) {
      resData = await res.json()
      throw new FEApiError({
        ok: res.ok,
        status: res.status,
        errors: resData.errors || null,
        message:
          Reflect.has(resData, 'errors') && !!resData.errors.length
            ? null
            : resData.message || `Error fetching from: ${res.url}`,
      })
    } else {
      if (!res.ok && overrideErrorsHandling) {
        return res as T
      }
      switch (responseType) {
        case 'csv':
          resData = await res.text()
          break
        default:
          resData = await res.json()
      }
    }

    return resData as T
  } catch (error) {
    if (retries > 0) {
      // eslint-disable-next-line
      console.warn(`Failed to fetch. Number of retries remaining: ${retries}`)

      fetchCsApiData(endpointOrUrl, fetchOptions, retries - 1)
    }
    console.error(error as Error)
    throw error
  }
}

export const digest = async (message: string, algo: string = 'SHA-256') => {
  return Array.from(new Uint8Array(await crypto.subtle.digest(algo, new TextEncoder().encode(message))), byte =>
    byte.toString(16).padStart(2, '0'),
  ).join('')
}

export const createCsApiUrlFromEndpoint = (endpoint: string) => {
  if (!process.env.REACT_APP_CS_API_URL) throw new Error('API Host Not Found, Check Environment Variables')
  const sanitizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
  const newUrl = new URL(`${process.env.REACT_APP_CS_API_URL}/${sanitizedEndpoint}` || '')
  return newUrl
}

interface EntityIdLookupOptions {
  baseUrl: string
  shouldPluraliseEntityName?: boolean
}

/**
 * A function to parse the entity id from a url
 * @param endpointOrUrl the endpoint or url to check for
 * @param entity the entity to get the id of
 * @param options the options for the lookup
 * @returns the entity id if found, otherwise null
 *
 * @example
 * const { osContextValue } = useContext(appContext)
 * const url = https://vmlyrc.wpp-stage.os-dev.io/application/f6564b07-eb41-42fc-aec4-2f62c9cf188f/pdp-generator-non-prod_0a1c0b0f-92dd-1f4f-8193-2a41c4830375_pdp-generator-qa/project/67ac758728b89e393a847a24/product/67ac7861de5251e04c33fcaf
 * const entityId = getEntityIdFromUrl(url, 'project', { baseUrl: osContextValue?.osContext.baseUrl || '' })
 * console.log(entityId) // 67ac758728b89e393a847a24
 */
export const getEntityIdFromUrl = (endpointOrUrl: AmbiguousURLType, entity: string, options: EntityIdLookupOptions) => {
  const endpointUrl = getUrlFromEndpoint(endpointOrUrl)
  const pathname = endpointUrl.pathname
  const { baseUrl, shouldPluraliseEntityName } = options

  const entityVariations = [entity]
  if (shouldPluraliseEntityName) {
    entityVariations.forEach(entity => entityVariations.push(getPlural(entity)))
  }

  // Wpp Open apps also have a similar Object Id structure, so it needs to be removed from the pathname before working with it
  const appPathname = endpointUrl.pathname.startsWith(`/${baseUrl}`)
    ? pathname.split(`/${baseUrl}`)[1]
    : endpointUrl.pathname

  const urlParts = appPathname.split('/')
  let matchedEntityId = null
  entityVariations.some(entity => {
    const entityIndex = urlParts.indexOf(entity)
    if (entityIndex > -1) {
      matchedEntityId = urlParts[entityIndex + 1]
      return true
    }
    return false
  })

  return matchedEntityId
}

/**
 * A function to check if the client id is detected in the request by searching the query params and body for client ids
 * Note that this does not check the pathname for client ids, only the query params and body
 * A console log will be printed if the client id is not detected in the request, reminding the user to check the pathname
 * @param endpointOrUrl the endpoint or url to check for client ids
 * @param fetchOptions the fetch options to check the body for client ids and the headers for the client ids header
 * @returns returns void but throws an error if the client id is not detected in the request
 */
export const checkRequestForClientIds = (
  endpointOrUrl: AmbiguousURLType,
  baseUrl: string,
  fetchOptions: Partial<RequestInit> = {},
) => {
  const endpointUrl = getUrlFromEndpoint(endpointOrUrl)
  let isClientIdDetected = false

  const clientVariations = ['client', 'clients', 'client_id', 'client_ids', 'clientId', 'clientIds']

  // check if client id is detected in the query params
  clientVariations.forEach(clientKey => {
    if (endpointUrl.searchParams.get(clientKey)) {
      isClientIdDetected = true
      return
    }
  })

  // check if an object id matching the same format of a Wpp Open client id is detected in the pathname and warn the developer if so
  if (!isClientIdDetected) {
    if (!['production'].includes(process.env.REACT_APP_BUILD_ENV || '')) {
      const clientObjectIdPattern =
        /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}/

      const pathname = endpointUrl.pathname
      // Wpp Open apps also have a similar Object Id structure, so it needs to be removed from the pathname when checking for client ids
      const appPathname = endpointUrl.pathname.startsWith(`/${baseUrl}`)
        ? pathname.split(`/${baseUrl}`)[1]
        : endpointUrl.pathname

      if (clientObjectIdPattern.test(appPathname || '')) {
        console.warn(
          'A potential client object id is present in the pathname, but no client id header was detected in the request',
        )
        // no need to throw an error here in-case of false positives
        return
      }
    }
  }

  // check if client id is detected in the body
  if (!isClientIdDetected && fetchOptions.body && typeof fetchOptions.body === 'string') {
    const body = JSON.parse(fetchOptions.body)
    clientVariations.forEach(clientKey => {
      if (Reflect.has(body, clientKey)) {
        isClientIdDetected = true
        return
      }
    })
  }

  if (isClientIdDetected && !Reflect.has(fetchOptions?.headers || {}, PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER)) {
    throw new Error(`${PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER} header not found in the request headers`)
  }
}

// REMOVE: This function has been moved to @shared/utils/utils.ts
/**
 * shorthand for 'arg as T'
 * @param arg argument to be cast
 * @returns argumentIn cast to specified type
 */
export function getType<T>(arg: T): T {
  return arg as T
}

// REMOVE: This function has been moved to @shared/utils/utils.ts
/**
 * shorthand for 'arg as keyof T'
 * e.g. getTypeKey\<Job>('jobId')
 * @param arg argument to be cast as a key of T
 * @returns argumentIn cast to keyof specified type
 */
export function getTypeKey<T>(arg: keyof T): keyof T {
  return arg as keyof T
}

export const snakeCaseToSentenceCase = (snakeCase: string) => {
  return snakeCase.charAt(0).toUpperCase() + snakeCase.slice(1).replace(/_/g, ' ')
}

export const downloadCSVFile = (data: string, filename: string) => {
  const blob = new Blob([data], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)

  // Create a temporary link element to trigger the download
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)

  // Trigger the download
  document.body.appendChild(link)
  link.click()

  // Clean up by revoking the object URL
  window.URL.revokeObjectURL(url)
}

export function updateSort<T extends string>(url: URL, sortObject: SetSortByPayload<T> | null) {
  const sortByKey = 'sort_by'
  const sortOrderKey = 'sort_order'

  if (sortObject === null) {
    url.searchParams.delete(sortByKey)
    url.searchParams.delete(sortOrderKey)
  } else {
    url.searchParams.set(sortByKey, sortObject.column)
    url.searchParams.set(sortOrderKey, sortObject.sort)
  }

  url.searchParams.set('position', '0')
  return new URL(url)
}

export function updateSortByQueryParams<T extends string>(
  urlQueryParams: URLSearchParams,
  sortObject: SetSortByPayload<T> | null,
) {
  const sortByKey = 'sort_by'
  const sortOrderKey = 'sort_order'

  if (sortObject === null) {
    urlQueryParams.delete(sortByKey)
    urlQueryParams.delete(sortOrderKey)
  } else {
    urlQueryParams.set(sortByKey, sortObject.column)
    urlQueryParams.set(sortOrderKey, sortObject.sort)
  }

  urlQueryParams.set('position', '0')
  return new URLSearchParams(urlQueryParams)
}

export function handleSortingUpdate<T>(
  sortConfig: SortingDetails | undefined,
  updateSort: React.Dispatch<React.SetStateAction<SetSortByPayload<T> | null>>,
) {
  if (!sortConfig) {
    updateSort(null)
    return
  }

  updateSort({
    column: sortConfig.column as T,
    sort: `${sortConfig.sort as 'asc' | 'desc'}ending`,
  })
}

export const getDefaultPaginationSearchParams = (limit: number) => {
  return new URLSearchParams({ position: '0', limit: limit.toString() })
}

// transform date from a date string to a formatted date string
// from: 2024-05-24T11:08:26.049Z
// to: 24/05/2024, 11:08 AM
const options: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
}
export function getFormattedDate(date?: Date | string) {
  if (!date) return 'INVALID DATE'

  if (typeof date === 'string') {
    date = new Date(date)
  }

  const newDate = date.toLocaleDateString('en-GB', options)
  return newDate.replace(',', ' at')
}

// Add 1 to the index to make it 1-indexed
const getOneIndexedPosition = (index: number) => index + 1

export const clampGridPosition = (index: number, columnCount: number = 3) => {
  const currentPosition = getOneIndexedPosition(index)

  // Calculate the row number (1-indexed)
  const currentRow = Math.ceil(currentPosition / columnCount)

  // Calculate the position of the last item in the current row
  const lastPositionInRow = currentRow * columnCount

  // Apply offset if needed
  return lastPositionInRow
}

export const sortAlphabetically = (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase())
export const sortNumerically = (a: number, b: number) => a - b
export const sortAlphanumerically = (a: string | number, b: string | number) => {
  if (typeof a === 'string' && typeof b === 'string') {
    return sortAlphabetically(a, b)
  } else if (typeof a === 'number' && typeof b === 'number') {
    return sortNumerically(a, b)
  } else if (typeof a === 'string' && typeof b === 'number') {
    return 1
  } else if (typeof a === 'number' && typeof b === 'string') {
    return -1
  } else {
    return 0
  }
}

export const transformStringToId = (string: string) => {
  return string
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

// Get the client and brand name from the clients data
export const getClientAndBrand = (
  clientsData: ClientsListDataV5 | null,
  clientId: string | undefined,
  brandId: string | undefined,
): ClientAndBrandName => {
  const clientDetails = clientsData?.clients?.find(client => client.id === clientId)
  const brandDetails = clientDetails?.brands?.find(brand => brand.id === brandId)

  return { client_name: clientDetails?.client_name || unknown, brand_name: brandDetails?.brand_name || unknown }
}

// Transform the task type to a more readable format
export const transformTypesToTaskType = (
  type: 'standard_content' | 'retailer_adaption' | 'sku_adaption' | undefined,
) => {
  if (!type) return unknown

  switch (type) {
    case 'standard_content':
      return 'Standard content'
    case 'retailer_adaption':
      return 'Retailer adaption'
    case 'sku_adaption':
      return 'SKU adaption'
    default:
      return type
  }
}

export const pluralise = (stringIn: string, count: number | undefined) => {
  return stringIn + (count === 1 ? '' : 's')
}

export const getPlural = (stringIn: string) => {
  return pluralise(stringIn, 2)
}

/**
 * Cross references two arrays of objects and returns the objects from the lookup array, that share a matching id from a field in the reference array
 *
 * @param lookupArray - The array for which you want to return the cross referenced objects
 * @param referenceArray - The array that contains the objects to cross reference
 * @returns The objects from the lookup array that share a matching id from a field in the reference array
 * @example
 * ```typescript
 * // Example usage of the function
 * const lookupArray = [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }];
 * const referenceArray = [{ id: 1, age: 25 }];
 * const result = myFunction(lookupArray, referenceArray);
 * result; // [{ id: 1, name: 'John' }]
 * ```
 * @template T - An array of objects that must contain an id field.
 * @template U - Also an array of objects that must contain an id field.
 */
export function crossReferenceArray<T extends { id?: string }, U extends { id?: string }>(
  lookupArray: T[],
  referenceArray: U[],
): T[] {
  return lookupArray.filter(({ id: lookupId }) => {
    const matchedFilterClientId = referenceArray?.find(({ id: referenceId }) => referenceId === lookupId)
    return !!matchedFilterClientId
  })
}

type FormDataType = NonNullable<UploadsCreateData['form_data'] | ImportsCreateResult['import_form_data']>
/**
 * This function uploads a file to a specified URL using FormData.
 *
 * @param {Object} formDataDetails - An object containing the necessary details for the FormData.
 *                                   It should include properties like 'bucket', 'X-Amz-Algorithm',
 *                                   'X-Amz-Credential', 'X-Amz-Date', 'key', 'X-Amz-Security-Token',
 *                                   'Policy', and 'X-Amz-Signature'.
 * @param {File} file - The file to be uploaded.
 * @param {string} url - The URL where the file will be uploaded.
 *
 * @returns {Promise<Response>} A Promise that resolves to the Response of the fetch request.
 *
 * @throws Will throw an error if the fetch request fails.
 */
export async function uploadFileToBucket(
  formDataDetails: FormDataType,
  file: File,
  url: string,
  extraFormDataDetails?: Record<string, string>,
) {
  const formData = new FormData()
  formData.append('bucket', formDataDetails.bucket as string)
  formData.append('X-Amz-Algorithm', formDataDetails['X-Amz-Algorithm'] as string)
  formData.append('X-Amz-Credential', formDataDetails['X-Amz-Credential'] as string)
  formData.append('X-Amz-Date', formDataDetails['X-Amz-Date'] as string)
  formData.append('key', formDataDetails.key as string)
  formData.append('X-Amz-Security-Token', formDataDetails['X-Amz-Security-Token'] as string)
  formData.append('Policy', formDataDetails.Policy as string)
  formData.append('X-Amz-Signature', formDataDetails['X-Amz-Signature'] as string)
  formData.append('file', file)
  extraFormDataDetails && Object.entries(extraFormDataDetails).forEach(([key, value]) => formData.append(key, value))

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
  })

  return response
}
